import React from "react";
import { Text, TouchableOpacity, View, Dimensions } from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import Avatar from "./Avatar";

export const AvatarLookComponent = ({
  selectedAvatarDetails,
  selectedAvatarPart,
  handleAvatarPartSelection,
  setSelectedAvatarPart,
  changeAvatar = false,
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const screenWidth = Dimensions.get("window").width;

  // Calculate preview size based on screen width for mobile
  const getPreviewSize = () => {
    if (deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM) {
      // 3 previews + gaps should fit in screen width with padding
      return Math.floor((screenWidth - 80) / 3); // 80 = padding + gaps
    }
    return undefined; // Use default from styles
  };
  const previewSize = getPreviewSize();

  return (
    <View
      style={[
        styles.selectedAvatarContainer,
        changeAvatar && { width: "100%", padding: 0 },
      ]}
    >
      {selectedAvatarDetails ? (
        <>
          <Text style={[AppFontStyle.BOLD_ANYBODY_15, styles.avatarLookText]}>
            Avatar Look
          </Text>
          <View
            style={[
              styles.avatarLookSection,
              changeAvatar && {
                marginLeft: 0,
                flexDirection: "row",
                justifyContent: "center",
              },
              // Add horizontal scroll for very small screens (fold phones)
              screenWidth < 350 && { overflow: 'scroll' },
            ]}
          >
            {/* Head */}
            <View
              style={[
                styles.avatarPreview,
                changeAvatar && { flexDirection: "column" },
              ]}
            >
              <TouchableOpacity
                accessible={false}
                onPress={() => {
                  handleAvatarPartSelection(selectedAvatarDetails.full);
                  setSelectedAvatarPart("head");
                }}
                style={[styles.previewImage, previewSize && { width: previewSize, height: previewSize }]}
              >
                {/* Head image*/}
                <Avatar
                  variant="head"
                  src={selectedAvatarDetails.full}
                  changeAvatar={changeAvatar}
                />
                {selectedAvatarPart === "head" && (
                  <View
                    style={{
                      width: 15,
                      height: 15,
                      borderRadius: 50,
                      backgroundColor: AppColors.white,
                      position: "absolute",
                      top: 8,
                      right: 8,
                    }}
                  />
                )}
              </TouchableOpacity>
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.SEMIBOLD_ANYBODY_14
                    : AppFontStyle.BOLD_ANYBODY_15,
                  styles.previewLabel,
                ]}
              >
                Head
              </Text>
            </View>
            {/* Upper */}
            <View
              style={[
                styles.avatarPreview,
                changeAvatar && { flexDirection: "column" },
              ]}
            >
              <TouchableOpacity
                accessible={false}
                onPress={() => {
                  handleAvatarPartSelection(selectedAvatarDetails.full);
                  setSelectedAvatarPart("upper");
                }}
                style={[styles.previewImage, previewSize && { width: previewSize, height: previewSize }]}
              >
                {/* Upper image*/}
                <Avatar
                  variant="upper"
                  src={selectedAvatarDetails.full}
                  changeAvatar={changeAvatar}
                />

                {selectedAvatarPart === "upper" && (
                  <View
                    style={{
                      width: 15,
                      height: 15,
                      borderRadius: 50,
                      backgroundColor: AppColors.white,
                      position: "absolute",
                      top: 8,
                      right: 8,
                    }}
                  />
                )}
              </TouchableOpacity>
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.SEMIBOLD_ANYBODY_14
                    : AppFontStyle.BOLD_ANYBODY_15,

                  styles.previewLabel,
                ]}
              >
                Upper Body
              </Text>
            </View>
            {/* Full body */}
            <View
              style={[
                styles.avatarPreview,
                changeAvatar && { flexDirection: "column" },
              ]}
            >
              <TouchableOpacity
                accessible={false}
                onPress={() => {
                  handleAvatarPartSelection(selectedAvatarDetails.full);
                  setSelectedAvatarPart("full");
                }}
                style={[styles.previewImage, previewSize && { width: previewSize, height: previewSize }]}
              >
                <Avatar
                  variant="full"
                  src={selectedAvatarDetails.full}
                  changeAvatar={changeAvatar}
                />
                {selectedAvatarPart === "full" && (
                  <View
                    style={{
                      width: 15,
                      height: 15,
                      borderRadius: 50,
                      backgroundColor: AppColors.white,
                      position: "absolute",
                      top: 8,
                      right: 8,
                    }}
                  />
                )}
              </TouchableOpacity>
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.SEMIBOLD_ANYBODY_14
                    : AppFontStyle.BOLD_ANYBODY_15,
                  styles.previewLabel,
                ]}
              >
                Full Body
              </Text>
            </View>
          </View>
        </>
      ) : (
        <Text style={styles.noAvatarText}>No Avatar Selected</Text>
      )}
    </View>
  );
};
const useStyles = CreateResponsiveStyle(
  {
    selectedAvatarContainer: {
      marginTop: 20,
      backgroundColor: "transparent",
      padding: 16,
      borderRadius: 12,
      alignItems: "flex-start",
      width: 375,
    },
    avatarLookText: {
      marginBottom: 12,
    },
    avatarLookSection: {
      justifyContent: "space-around",
      marginLeft: 50,
      width: "100%",
      gap: 20,
    },
    avatarPreview: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      marginHorizontal: 8,
      gap: 10,
    },
    previewLabel: {
      color: AppColors.white,
      marginBottom: 8,
    },
    previewImage: {
      borderRadius: 8,
      borderWidth: 1,
      borderColor: AppColors.white,
      overflow: "hidden",
    },
    noAvatarText: {
      color: AppColors.white,
      fontSize: 16,
      fontStyle: "italic",
    },
  },
  {
    [DEVICE_SIZES.SM]: {
      selectedAvatarContainer: {
        marginTop: 0,
        backgroundColor: "transparent",
        padding: 5,
        borderRadius: 12,
        alignItems: "flex-start",
        width: "100%",
      },
      avatarLookText: {
        marginBottom: 18,
      },
      avatarPreview: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginHorizontal: 8,
      },
      avatarLookSection: {
        justifyContent: "space-around",
        width: "100%",
        gap: 0,
        marginLeft: 0,
        display: "flex",
        flexDirection: "row",
      },
      previewImage: {
        width: '28%',
        aspectRatio: 1,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: AppColors.white,
        overflow: "hidden",
      },
    },
    [DEVICE_SIZES.XS]: {
      selectedAvatarContainer: {
        marginTop: 0,
        backgroundColor: "transparent",
        padding: 5,
        borderRadius: 12,
        alignItems: "flex-start",
        width: "100%",
      },
      avatarLookText: {
        marginBottom: 12,
      },
      avatarPreview: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginHorizontal: 8,
      },
      avatarLookSection: {
        justifyContent: "space-around",
        width: "100%",
        gap: 0,
        marginLeft: 0,
        display: "flex",
        flexDirection: "row",
      },
      previewImage: {
        width: '28%',
        aspectRatio: 1,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: AppColors.white,
        overflow: "hidden",
      },
    },
  }
);
